/**
 * lib/sync/paginate.ts
 *
 * ESCALA 0A/0B — Motor de sincronización PAGINADA, RESUMIBLE, con INSTANTÁNEA,
 * IDENTIDAD DE ALCANCE e INCREMENTAL real.
 *
 * ESCALA 0B refuerza:
 *   • IDENTIDAD (Bloque 1): cada corrida se identifica por un SCOPE seguro
 *     (entity + userId + business + store + schemaVersion). scopeFingerprint es
 *     determinista y NO usa email. Un run de otra identidad jamás se reanuda.
 *   • TRES ESTADOS (Bloque 2): resume cursor (temporal) ≠ snapshot boundary
 *     (frontera server-side) ≠ completed checkpoint (confirmado; origen del
 *     próximo delta). El checkpoint SOLO se promueve al completar.
 *   • FRESCURA HONESTA (Bloque 3): el resultado expone `completed` y `freshAt`
 *     (timestamp server-side = boundary). El caller SOLO marca fresco si completó.
 *   • INCREMENTAL (Bloque 4): una corrida incremental arranca desde el completed
 *     checkpoint; full_reconciliation arranca desde cero con snapshot propio.
 *
 * El servidor sigue siendo la autoridad de datos y de RLS: aquí solo se ordena y
 * pagina lo que el servidor ya autoriza a leer.
 */
import type { PostgrestError } from '@supabase/supabase-js'
import type { Table } from 'dexie'
import { db } from '@lib/dexie/schema'
import type { SyncRun, SyncRunMode } from '@lib/dexie/schema'

/** Cursor keyset compuesto (updated_at, id). */
export interface KeysetCursor {
  updatedAt: string // ISO
  id: string // uuid u otra columna de desempate
}

/** Identidad segura de una corrida. NUNCA usar email como identidad. */
export interface SyncScope {
  entity: string
  userId: string // auth/user id estable
  businessId: string | null
  storeId: string | null
  schemaVersion: number
  permissionRevision?: string | null
}

/** Huella determinista del alcance = clave del run. No usa email. */
export function scopeFingerprint(s: SyncScope): string {
  return [
    s.entity,
    s.userId,
    s.businessId ?? '_',
    s.storeId ?? '_',
    `v${s.schemaVersion}`,
    s.permissionRevision ?? '-',
  ].join('|')
}

export interface PageRequest {
  boundary: string
  cursor: KeysetCursor | null
  pageSize: number
}

export interface PaginatedSyncConfig<TRow> {
  scope: SyncScope
  /** 'incremental' (desde checkpoint) o 'full_reconciliation' (desde cero). */
  mode: SyncRunMode
  pageSize?: number
  maxPages?: number
  /**
   * Si true, la corrida NUNCA se reanuda desde un cursor previo: siempre arranca
   * desde el origen (checkpoint en incremental, cero en full). Necesario cuando la
   * persistencia es ATÓMICA (acumular en memoria + promover al completar): como los
   * datos no se guardan por página, reanudar desde un cursor intermedio perdería las
   * páginas no persistidas y una purga con `seen` incompleto borraría filas válidas.
   */
  noResume?: boolean
  /** Frontera de instantánea: max(updated_at) del servidor para este alcance. */
  serverBoundary: () => Promise<{ boundary: string | null } | { error: PostgrestError }>
  /** Descarga UNA página keyset (updated_at, id) asc, > cursor y <= boundary. */
  fetchPage: (req: PageRequest) => Promise<{ rows: TRow[] } | { error: PostgrestError }>
  cursorOf: (row: TRow) => KeysetCursor
  /** Persiste (idempotente, bulkPut por PK) las filas de una página. */
  persistPage: (rows: TRow[]) => Promise<number>
  /**
   * Fase B (propiedad por página): se consulta ANTES de descargar/persistir cada
   * página. Si devuelve false (la pestaña perdió el lock/lease), la corrida se marca
   * `interrupted` sin promover checkpoint → una pestaña que ya no es dueña nunca
   * corrompe el estado. Por defecto (sin lock) siempre continúa.
   */
  shouldContinue?: () => Promise<boolean>
  /**
   * PROMOCIÓN ATÓMICA TOTAL (0B.9 P1#2). Al COMPLETAR, todo el cierre ocurre en UNA
   * sola transacción Dexie: datos + purga (`promote`) + sync_run + checkpoint +
   * freshness (sync_meta). Antes el sync_run/checkpoint y la freshness se escribían
   * FUERA de la transacción de datos → una caída entre medias dejaba estado desgarrado
   * (datos publicados sin checkpoint, o freshness avanzada sin datos).
   * `promote` corre DENTRO de la transacción; si lanza, no se publica nada.
   */
  promote?: () => Promise<void>
  /** Tablas Dexie que toca `promote` (entran en el scope de la transacción). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- scope heterogéneo de tablas Dexie
  promoteTables?: Table<any, any>[]
  /** Si se indica, sync_meta[entity] se marca fresco DENTRO de la misma transacción. */
  freshnessEntity?: string
}

export interface PaginatedSyncResult {
  entity: string
  scopeId: string
  mode: SyncRunMode
  status: SyncRun['status']
  pagesCompleted: number
  recordsReceived: number
  recordsUpserted: number
  /** true SOLO si la corrida terminó (hasMore=false). Base de la frescura. */
  completed: boolean
  /** Marcador de frescura server-side (= boundary) SOLO si completó; si no, null. */
  freshAt: string | null
  boundary: string | null
  /** Checkpoint confirmado tras completar (origen del próximo delta). */
  checkpoint: KeysetCursor | null
  error: string | null
}

const DEFAULT_PAGE_SIZE = 500
const DEFAULT_MAX_PAGES = 100_000

function checkpointOf(run: SyncRun | undefined): KeysetCursor | null {
  if (run?.completed_checkpoint_updated_at && run.completed_checkpoint_id) {
    return { updatedAt: run.completed_checkpoint_updated_at, id: run.completed_checkpoint_id }
  }
  return null
}

/**
 * Ejecuta (o reanuda) una sincronización paginada de una entidad para un scope.
 * Segura de llamar repetidamente: reanuda interrumpidas, no duplica, y en modo
 * incremental no re-descarga el catálogo completo.
 */
export async function runPaginatedSync<TRow>(
  cfg: PaginatedSyncConfig<TRow>
): Promise<PaginatedSyncResult> {
  const pageSize = cfg.pageSize ?? DEFAULT_PAGE_SIZE
  const maxPages = cfg.maxPages ?? DEFAULT_MAX_PAGES
  const id = scopeFingerprint(cfg.scope)
  const now = Date.now()

  const prior = await db.sync_runs.get(id).catch(() => undefined)
  // schemaVersion incompatible invalida cualquier resume/checkpoint previo.
  const priorCompatible = prior && prior.schema_version === cfg.scope.schemaVersion
  const priorCheckpoint = priorCompatible ? checkpointOf(prior) : null

  /**
   * PROPIEDAD POR FENCING TOKEN (0B.13). Identidad de este intento + el token que
   * obtuvo al adquirir. El RELOJ NO PARTICIPA en ninguna decisión de propiedad.
   *
   * El diseño anterior comparaba `started_at`, que no es un CAS ni define un orden
   * total: dos intentos con el mismo `Date.now()` empataban y el viejo podía reclamar
   * el registro del nuevo. Ahora la propiedad es un contador monotónico POR SCOPE que
   * vive en el propio registro y sólo avanza mediante compare-and-swap transaccional.
   */
  const attemptId = `${now.toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  /** Token que este intento observó ANTES de tocar nada (base del CAS de adquisición). */
  const observedToken = prior?.fencing_token ?? 0
  /** Token propio tras adquirir. 0 = aún no somos dueños de nada. */
  let myToken = 0

  /**
   * ADQUISICIÓN: compare-and-swap del token (observado → observado+1) dentro de UNA
   * transacción. Si el token almacenado ya no es el que observamos, significa que otro
   * intento adquirió mientras tanto (típicamente durante nuestra espera de red) y este
   * intento se RETIRA SIN ESCRIBIR. No existe reclamación incondicional.
   */
  const acquire = async (build: () => SyncRun): Promise<boolean> => {
    try {
      return await db.transaction('rw', db.sync_runs, async () => {
        const cur = await db.sync_runs.get(id)
        const curToken = cur?.fencing_token ?? 0
        // Revalidación del token observado: si cambió, perdimos la carrera.
        if (curToken !== observedToken) return false
        const token = curToken + 1
        await db.sync_runs.put({ ...build(), attempt_id: attemptId, fencing_token: token })
        myToken = token
        return true
      })
    } catch {
      return false
    }
  }

  /**
   * Escritura posterior: exige seguir siendo el MISMO intento Y el MISMO token. Un
   * intento con token inferior no puede completar, interrumpir, marcar error, mover el
   * checkpoint ni la frescura: simplemente no escribe. Devuelve si llegó a escribir.
   */
  const putRun = async (next: SyncRun): Promise<boolean> => {
    if (myToken === 0) return false // nunca adquirimos: no somos dueños de nada
    try {
      return await db.transaction('rw', db.sync_runs, async () => {
        const cur = await db.sync_runs.get(id)
        if (!cur || cur.attempt_id !== attemptId || cur.fencing_token !== myToken) return false
        await db.sync_runs.put({ ...next, attempt_id: attemptId, fencing_token: myToken })
        return true
      })
    } catch {
      return false
    }
  }

  /**
   * ¿Seguimos en condiciones de adquirir tras una operación de RED? (0B.12 O2 / 0B.13)
   * Revalida el contexto/propiedad del lease y el TOKEN OBSERVADO. Sin reloj.
   */
  const canAcquireAfterNetwork = async (): Promise<boolean> => {
    if (cfg.shouldContinue && !(await cfg.shouldContinue())) return false
    const cur = await db.sync_runs.get(id).catch(() => undefined)
    return (cur?.fencing_token ?? 0) === observedToken
  }

  const result = (over: Partial<PaginatedSyncResult>, run: SyncRun): PaginatedSyncResult => ({
    entity: cfg.scope.entity,
    scopeId: id,
    mode: cfg.mode,
    status: run.status,
    pagesCompleted: run.pages_completed,
    recordsReceived: run.records_received,
    recordsUpserted: run.records_upserted,
    completed: run.status === 'completed',
    freshAt: run.status === 'completed' ? run.snapshot_boundary : null,
    boundary: run.snapshot_boundary,
    checkpoint: checkpointOf(run),
    error: run.last_error,
    ...over,
  })

  // ── Reanudar corrida interrumpida del MISMO scope/modo, o iniciar nueva ──────
  // noResume la desactiva (persistencia atómica: reanudar perdería páginas).
  const resumable =
    !cfg.noResume &&
    priorCompatible &&
    prior!.status === 'interrupted' &&
    prior!.mode === cfg.mode &&
    prior!.snapshot_boundary !== null

  let boundary: string | null
  let cursor: KeysetCursor | null
  let run: SyncRun

  if (resumable && prior) {
    boundary = prior.snapshot_boundary
    // Reanuda desde el resume cursor; si aún no había página, desde el origen
    // (checkpoint en incremental, cero en full).
    cursor =
      prior.resume_cursor_updated_at && prior.resume_cursor_id
        ? { updatedAt: prior.resume_cursor_updated_at, id: prior.resume_cursor_id }
        : cfg.mode === 'incremental'
          ? priorCheckpoint
          : null
    run = { ...prior, status: 'running', last_error: null }
    if (!(await acquire(() => run))) {
      const lost: SyncRun = { ...run, status: 'interrupted', last_error: 'TOKEN_PERDIDO' }
      return result({ error: 'TOKEN_PERDIDO' }, lost)
    }
  } else {
    const b = await cfg.serverBoundary()
    if ('error' in b) {
      const failed: SyncRun = {
        id,
        entity: cfg.scope.entity,
        user_id: cfg.scope.userId,
        business_id: cfg.scope.businessId,
        store_id: cfg.scope.storeId,
        mode: cfg.mode,
        snapshot_boundary: prior?.snapshot_boundary ?? null,
        resume_cursor_updated_at: prior?.resume_cursor_updated_at ?? null,
        resume_cursor_id: prior?.resume_cursor_id ?? null,
        completed_checkpoint_updated_at: priorCheckpoint?.updatedAt ?? null,
        completed_checkpoint_id: priorCheckpoint?.id ?? null,
        pages_completed: 0,
        records_received: 0,
        records_upserted: 0,
        status: 'failed',
        last_error: b.error.message,
        retry_count: (prior?.retry_count ?? 0) + 1,
        started_at: now,
        completed_at: null,
        schema_version: cfg.scope.schemaVersion,
      }
      // 0B.12 O2: este write ocurre DESPUÉS de una llamada de red; sólo se reclama si
      // seguimos siendo dueños y nadie más reclamó entre medias.
      // Tras la RED: sólo se escribe el fallo si aún podemos adquirir (token intacto).
      if (await canAcquireAfterNetwork()) await acquire(() => failed)
      return result({ error: b.error.message }, failed)
    }
    boundary = b.boundary
    // Origen: incremental arranca desde el checkpoint confirmado; full desde cero.
    cursor = cfg.mode === 'incremental' ? priorCheckpoint : null
    run = {
      id,
      entity: cfg.scope.entity,
      user_id: cfg.scope.userId,
      business_id: cfg.scope.businessId,
      store_id: cfg.scope.storeId,
      mode: cfg.mode,
      snapshot_boundary: boundary,
      resume_cursor_updated_at: cursor?.updatedAt ?? null,
      resume_cursor_id: cursor?.id ?? null,
      // El checkpoint NO se toca hasta completar; incremental preserva el previo.
      completed_checkpoint_updated_at: priorCheckpoint?.updatedAt ?? null,
      completed_checkpoint_id: priorCheckpoint?.id ?? null,
      pages_completed: 0,
      records_received: 0,
      records_upserted: 0,
      status: 'running',
      last_error: null,
      retry_count: 0,
      started_at: now,
      completed_at: null,
      schema_version: cfg.scope.schemaVersion,
    }
    // 0B.12 O2: entre serverBoundary() (red, potencialmente lenta) y esta reclamación
    // el lease pudo vencer y otra pestaña arrancar su corrida. Antes se reclamaba a
    // ciegas y el intento viejo SUSTITUÍA al nuevo. Ahora se comprueba contexto,
    // propiedad y ausencia de un intento ajeno más reciente; si no, se aborta.
    // 0B.13: entre serverBoundary() y esta adquisición otro intento pudo adquirir. Se
    // revalida el TOKEN OBSERVADO y se adquiere por CAS; nunca de forma incondicional.
    if (!(await canAcquireAfterNetwork()) || !(await acquire(() => run))) {
      const aborted: SyncRun = { ...run, status: 'interrupted', last_error: 'TOKEN_PERDIDO' }
      return result({ error: 'TOKEN_PERDIDO' }, aborted)
    }
  }

  /**
   * CIERRE ATÓMICO TOTAL (0B.9 P1#2): datos + purga + sync_run + checkpoint +
   * freshness en UNA sola transacción Dexie. Si `promote` lanza, NADA se publica y el
   * run NO queda 'completed' → no hay estado desgarrado ni frescura mentirosa.
   */
  const commitCompleted = async (finalRun: SyncRun): Promise<void> => {
    // `app_meta` va SIEMPRE en el scope (0B.10 P1): el lease de respaldo (navegadores
    // sin Web Locks) vive ahí, y la comprobación de propiedad que corre DENTRO de
    // `promote` lo lee. Sin incluirlo, Dexie rechaza el acceso a una tabla fuera de la
    // transacción, `isOwner` lo interpretaba como "ya no soy dueño" y la promoción
    // abortaba SIEMPRE → sin Web Locks la sincronización jamás completaba.
    const tables = [db.sync_runs, db.sync_meta, db.app_meta, ...(cfg.promoteTables ?? [])]
    await db.transaction('rw', tables, async () => {
      // COMPARE-AND-SWAP también al COMPLETAR (0B.11 P1): si otra pestaña ya tomó el
      // registro (adquirió el lease y arrancó su corrida), este intento está obsoleto y
      // no puede publicar su instantánea encima. Lanzar aborta toda la transacción.
      const cur = await db.sync_runs.get(id)
      if (!cur || cur.attempt_id !== attemptId || cur.fencing_token !== myToken) {
        throw new Error('TOKEN_PERDIDO: otro intento adquirió el registro')
      }
      if (cfg.promote) await cfg.promote()
      await db.sync_runs.put({ ...finalRun, attempt_id: attemptId, fencing_token: myToken })
      if (cfg.freshnessEntity) {
        await db.sync_meta.put({
          table_name: cfg.freshnessEntity,
          last_sync_at: Date.now(),
          etag: null,
        })
      }
    })
  }

  /**
   * Cierre con RED DE SEGURIDAD (0B.10 P1): si la promoción falla (lease perdido,
   * contexto cambiado, ConstraintError…), la transacción revierte y el registro se
   * quedaría con el último estado escrito por página, que es `running` — es decir,
   * ETERNAMENTE running: ni completado ni reanudable (sólo se reanuda `interrupted`),
   * bloqueando el diagnóstico y la recuperación. Aquí se fuerza a `interrupted` con el
   * error, de modo que un run SIEMPRE termina en completed o interrupted.
   */
  const finishOrMarkInterrupted = async (
    finalRun: SyncRun
  ): Promise<{ ok: true } | { ok: false; run: SyncRun; error: string }> => {
    try {
      await commitCompleted(finalRun)
      return { ok: true }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      const failed: SyncRun = {
        ...finalRun,
        status: 'interrupted',
        completed_at: null,
        // El checkpoint NO avanza: la promoción no llegó a publicarse.
        completed_checkpoint_updated_at: priorCheckpoint?.updatedAt ?? null,
        completed_checkpoint_id: priorCheckpoint?.id ?? null,
        last_error: msg,
        retry_count: finalRun.retry_count + 1,
      }
      // 0B.12 O1: CAS, NUNCA reclamación. Si la promoción falló porque otro intento
      // tomó el registro, este intento está obsoleto y debe RETIRARSE SIN ESCRIBIR.
      const wrote = await putRun(failed)
      if (!wrote) console.warn('[paginate] intento obsoleto: no se marca interrupted', id)
      return { ok: false, run: failed, error: msg }
    }
  }

  // Sin frontera => tabla vacía para este alcance => completo SIN filas. Ojo: en
  // full_reconciliation esto significa "el servidor no autoriza nada aquí", así que la
  // promoción debe correr igual (purga con `seen` vacío = borra todo lo no autorizado).
  if (boundary === null) {
    run = { ...run, status: 'completed', completed_at: Date.now() }
    const done = await finishOrMarkInterrupted(run)
    if (!done.ok) return result({ error: done.error }, done.run)
    return result({}, run)
  }

  // El último cursor confirmado en ESTA corrida (para promover el checkpoint).
  let lastConfirmed: KeysetCursor | null =
    run.resume_cursor_updated_at && run.resume_cursor_id
      ? { updatedAt: run.resume_cursor_updated_at, id: run.resume_cursor_id }
      : null

  // RED DE SEGURIDAD GLOBAL (0B.11 P2): cualquier excepción del bucle —incluida una que
  // lance persistPage o fetchPage — debe dejar el run en 'interrupted'. Antes sólo
  // se cubría el fallo de la promoción, así que un throw en persistPage dejaba el
  // registro en 'running' ETERNO (no reanudable, sólo se reanuda 'interrupted').
  try {
    let safety = 0
    while (safety < maxPages) {
      safety++
      // PROPIEDAD (Fase B): si perdimos el lock/lease a media corrida, abortar SIN
      // promover (interrupted). Otra pestaña dueña completará; el estado no se corrompe.
      if (cfg.shouldContinue && !(await cfg.shouldContinue())) {
        run = { ...run, status: 'interrupted', last_error: 'LOCK_PERDIDO' }
        await putRun(run)
        return result({ error: 'LOCK_PERDIDO' }, run)
      }
      const page = await cfg.fetchPage({ boundary, cursor, pageSize })
      if ('error' in page) {
        run = {
          ...run,
          status: 'interrupted',
          last_error: page.error.message,
          retry_count: run.retry_count + 1,
        }
        await putRun(run)
        return result({ error: page.error.message }, run)
      }

      const rows = page.rows
      if (rows.length > 0) {
        // PROPIEDAD, 2ª comprobación (0B.9 P2#7): DESPUÉS del fetch y ANTES de persistir.
        // La descarga puede tardar y el lease expirar en ese hueco; sin esta comprobación
        // una pestaña que ya perdió la propiedad escribiría igualmente la página.
        if (cfg.shouldContinue && !(await cfg.shouldContinue())) {
          run = { ...run, status: 'interrupted', last_error: 'LOCK_PERDIDO' }
          await putRun(run)
          return result({ error: 'LOCK_PERDIDO' }, run)
        }
        const upserted = await cfg.persistPage(rows)
        const last = cfg.cursorOf(rows[rows.length - 1])
        cursor = last
        lastConfirmed = last
        run = {
          ...run,
          resume_cursor_updated_at: last.updatedAt,
          resume_cursor_id: last.id,
          pages_completed: run.pages_completed + 1,
          records_received: run.records_received + rows.length,
          records_upserted: run.records_upserted + upserted,
          last_error: null,
        }
        // Persistir el resume cursor DESPUÉS de la página (orden crítico: si muere
        // aquí, se reanuda desde el cursor previo y la página se reprocesa
        // idempotente, sin omitir ni duplicar). El checkpoint NO se toca aún.
        await putRun(run)
      }

      if (rows.length < pageSize) {
        // COMPLETADO: promover el último cursor confirmado a completed checkpoint.
        // Si no hubo filas (delta 0), se conserva el checkpoint previo.
        const promoted = lastConfirmed ?? priorCheckpoint
        run = {
          ...run,
          status: 'completed',
          completed_at: Date.now(),
          completed_checkpoint_updated_at: promoted?.updatedAt ?? null,
          completed_checkpoint_id: promoted?.id ?? null,
        }
        const done = await finishOrMarkInterrupted(run)
        if (!done.ok) return result({ error: done.error }, done.run)
        return result({}, run)
      }
    }

    run = { ...run, status: 'interrupted', last_error: 'MAX_PAGES_ALCANZADO' }
    await putRun(run)
    return result({ error: 'MAX_PAGES_ALCANZADO' }, run)
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    run = {
      ...run,
      status: 'interrupted',
      last_error: msg,
      retry_count: run.retry_count + 1,
      // El checkpoint NO avanza: no se llegó a publicar nada.
      completed_checkpoint_updated_at: priorCheckpoint?.updatedAt ?? null,
      completed_checkpoint_id: priorCheckpoint?.id ?? null,
    }
    const wrote = await putRun(run)
    if (!wrote) console.warn('[paginate] no se pudo marcar interrupted (intento obsoleto):', id)
    return result({ error: msg }, run)
  }
}

/**
 * Construye el predicado keyset `.or(...)` de PostgREST para (updated_at, idCol) > cursor.
 * `idCol` permite desempatar por una columna distinta de `id`. Sin cursor devuelve null.
 */
export function keysetOrFilter(cursor: KeysetCursor | null, idCol = 'id'): string | null {
  if (!cursor) return null
  return `updated_at.gt.${cursor.updatedAt},and(updated_at.eq.${cursor.updatedAt},${idCol}.gt.${cursor.id})`
}
